import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import moment from 'moment-timezone'
import useBrandConfig from '../../hooks/useBrandConfig'
import settings from '../settings'
import { Container as ContainerUI } from '../../components/grid'
import Progressbar from './Progressbar'
import useTranslation from '../../../utils/translation'
import Button from '../button/baseButton'
import Spinner from '../icons/spinner'


const Container = styled.div`
  background-color: #DFC6B3;
  -webkit-box-shadow: 0px -1px 11px 1px rgba(0,0,0,0.39);
  -moz-box-shadow: 0px -1px 11px 1px rgba(0,0,0,0.39);
  box-shadow: 0px -1px 11px 1px rgba(0,0,0,0.39);
  bottom: 0;
  position: fixed;
  left: 0;
  z-index: 11;
  width: 100%;
`

const InnerContainer = styled(ContainerUI)`
  height: 100px;
  ${(props) => props.theme.media.max.md`
    height: 80px;
  `}
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: ${theme('grid.containerMaxWidth')};
  padding: 0 20px;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: baseline;
  span, button {
    margin-left: 10px !important;
  }

  ${(props) => props.theme.media.max.md`
    justify-content: space-between;
    width: 100%;
  `}
`

const ProgressbarContainer = styled.div`
  ${(props) => props.theme.media.max.md`
    display: none;
  `}
`

const InformationTexts = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 5px;
` 

const StyledButton = styled(Button)`
    min-width: 100px;
    padding: 0;
`

const Saving = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`

const PrepProgressbar = ({
  appointment,
  questions,
  prepData,
  saving,
  lastSavedTime
}) => {
  const t = useTranslation('app.prep-form.progressbar')
  const brandConfig = useBrandConfig(settings.brand).data

  const [progressLevel, setProgressLevel] = useState(0)
  const [progressText, setProgressText] = useState("")
  const [completedSteps, setCompletedSteps] = useState(0)
  const [lastSaved, setLastSaved] = useState('')

  useEffect(() => {
    if (prepData) {
      const answers = prepData.questions.filter((item) => !!item.answers.length)
      const progress = answers.length / questions.length
      if (progress > 0.66) {
        setProgressLevel(3)
        setProgressText(t('progressText3'))
      } else if (progress > 0.33) {
        setProgressLevel(2)
        setProgressText(t('progressText2')) 
      } else {
        setProgressLevel(1)
        setProgressText(t('progressText1'))  
      }

      setCompletedSteps(answers.length)
    }
  }, [prepData])

  useEffect(() => {
    if (lastSavedTime) {
      const timeZone = brandConfig ? brandConfig.ianaTimeZoneId : 'UTC'
      const utcTime = moment.utc(lastSavedTime)
      const localTime = utcTime.tz(timeZone)
      setLastSaved(`${localTime.format('HH:mm')}`)
    }
    else if (appointment.appointment) {
      const timeZone = brandConfig ? brandConfig.ianaTimeZoneId : 'UTC'
      const utcTime = moment.utc(appointment.appointment.updated)
      const localTime = utcTime.tz(timeZone)
      setLastSaved(`${localTime.format('HH:mm')}`)
    }
  }, [appointment, brandConfig, lastSavedTime])

  return (
    <Container>
      <InnerContainer>
        <ProgressbarContainer>
          <InformationTexts>
            <span>{t('informationlevel')}:&nbsp;</span>
            <b>{progressText}</b>
          </InformationTexts>
          <Progressbar steps={questions.length+1} completedSteps={completedSteps+1} progressLevel={progressLevel} />
        </ProgressbarContainer>
        <ButtonContainer>
          <span>{t('lastsaved')}: {lastSaved}</span>
          <StyledButton disabled>
            {saving && (
              <Saving>
                <p>{t('save-button-saving')}</p>
                <Spinner width={20} height={20} />
              </Saving>
            )}
            {!saving && (
              <p>{t('save-button')}</p>
            )}
          </StyledButton>
        </ButtonContainer>
      </InnerContainer>
    </Container>
  )
}

PrepProgressbar.propTypes = {
  appointment: PropTypes.shape(),
  questions: PropTypes.arrayOf(PropTypes.shape()),
  prepData: PropTypes.shape(),
  saving: PropTypes.bool,
  lastSavedTime: PropTypes.string,
}

PrepProgressbar.defaultProps = {
  appointment: {},
  questions: [],
  prepData: {},
  saving: false,
  lastSavedTime: '',
}

export default PrepProgressbar
